'use client';
import { Box, Container, Heading, Text } from '@chakra-ui/react';
import cn from 'classnames';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import CustomImage from '@/app/_components/custom-image/CustomImage';
import LinkCustom from '@/app/_components/link/LinkCustom';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import { ReactComponent as ArrowBackwardCircleDark } from '@/app/_icons/ArrowBackwardCircleDark.svg';
import { ReactComponent as ArrowForwardCircleDark } from '@/app/_icons/ArrowForwardCircleDark.svg';
import { getImageLink } from '@/services/link.service';
import { capitalizeFirstLetter } from '@/services/string-format.service';
import { ImageTransformationKeys } from '@/types/files/files.types';
import { SpecificationsPricesType } from '@/types/new-cars/new-cars.types';

import Styles from './CompareModels.module.scss';

type Props = {
  specificationsPrices: SpecificationsPricesType[];
  modelName: string;
  hideCompareButton?: boolean;
  slug: string;
};

const imageKeys: ImageTransformationKeys = {
  desktopKey: 'new-car-meetthefamily-desktop',
  mobileKey: 'new-car-meetthefamily-mobile',
};

const renderPrice = (price: number | string | null | undefined) => {
  if (!price || price === 0 || price === '0') {
    return <Text className={Styles.titleXXl}>Price Coming Soon</Text>;
  }
  return <PriceFormat prefix="R" value={Number(price)} />;
};

const renderModelCard = (
  model: SpecificationsPricesType | null,
  index: number,
  modelName: string,
) => {
  if (!model) {
    return null;
  }
  return (
    <Box className={Styles.modelCard} key={index}>
      <Box className={Styles.modelCardImage}>
        {model?.image_id && getImageLink(model.image_id) && (
          <CustomImage
            imageKeys={imageKeys}
            imageSrc={getImageLink(model.image_id) ?? ''}
            width={232}
            height={140}
            alt={model.title}
            additionalParams={{
              cache: '30-10-2024',
              fit: 'inside',
            }}
          />
        )}
      </Box>
      <Box className={Styles.modelCardContent}>
        <Box>
          <Text fontSize="xs" className={Styles.modelName}>
            {modelName}
          </Text>
          <Text className={Styles.menuText}>
            {model.title
              .toLowerCase()
              .replace('kia ', '')
              .replace(`${modelName}`, '')
              .toUpperCase()}
          </Text>
        </Box>

        <Box>
          <Text fontSize="xs">Price</Text>
          <Box className={Styles.headingElement}>
            <Heading as="h3" className={Styles.titleXXl}>
              {renderPrice(model.price)}
            </Heading>
          </Box>
        </Box>

        {model?.fuel_type && (
          <Box>
            <Text fontSize="xs">Fuel Type</Text>
            <Box className={Styles.headingElement}>
              <Heading as="h3" className={Styles.titleXl}>
                {model.fuel_type}
              </Heading>
            </Box>
          </Box>
        )}

        {model?.feature &&
          model.feature.length > 0 &&
          model.feature.map((feature, featureIndex) =>
            feature?.title && feature?.description ? (
              <Box key={featureIndex}>
                <Text fontSize="xs">{feature.title}</Text>
                <Box className={Styles.headingElement}>
                  <Heading
                    as="h3"
                    className={cn(Styles.titleXl, Styles.fwNormal)}
                  >
                    {feature.description}
                  </Heading>
                </Box>
              </Box>
            ) : null,
          )}
      </Box>
    </Box>
  );
};
const useScrollTracker = (containerRef: React.RefObject<HTMLDivElement>) => {
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    isScrolling: false,
  });

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const checkScrollability = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;

      setScrollState({
        canScrollLeft: scrollLeft > 0,
        canScrollRight: scrollLeft < scrollWidth - clientWidth - 10,
        isScrolling: false,
      });
    };

    const debouncedCheckScrollability = debounce(checkScrollability, 200);

    const handleScroll = () => {
      setScrollState((prev) => ({ ...prev, isScrolling: true }));

      debouncedCheckScrollability();
    };

    container.addEventListener('scroll', handleScroll);

    checkScrollability();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      debouncedCheckScrollability.cancel();
    };
  }, []);

  return scrollState;
};

const smoothScroll = (
  container: HTMLDivElement,
  direction: 'left' | 'right',
) => {
  const scrollAmount = 300; // Adjust as needed

  container.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: 'smooth',
  });
};
const CompareModels = ({ specificationsPrices, modelName, hideCompareButton, slug }: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { canScrollLeft, canScrollRight, isScrolling } =
    useScrollTracker(scrollContainerRef);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      smoothScroll(scrollContainerRef.current, 'left');
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      smoothScroll(scrollContainerRef.current, 'right');
    }
  };
  const hasValidModels =
    specificationsPrices &&
    specificationsPrices.length > 0 &&
    specificationsPrices.some((model) => model !== null);

  const selectedModalMMCode = useMemo(() => {
    return specificationsPrices.reduce((result: string[], cur) => {
      if (cur && cur?.mm_code) {
        result.push(cur.mm_code);
      }
      return result;
    }, []);
  }, [specificationsPrices]);

  const memoizedModelCards = useMemo(
    () =>
      specificationsPrices.map((model, index) =>
        renderModelCard(model, index, modelName),
      ),
    [specificationsPrices, modelName],
  );

  if (!hasValidModels) return null;

  return (
    <Box
      className={Styles.compareModelWrapper}
      marginTop={{ base: 10, md: 20 }}
      marginBottom={{ base: 10, md: '100px' }}
    >
      <Container maxW={'1280'} width={'100%'}>
        <Box className={Styles.headingElement} marginBottom={14}>
          <Heading
            as="h2"
            className={Styles.titleLg}
            marginBottom={4}
            textAlign={'center'}
          >
            Meet the family.
          </Heading>
          <Text textAlign="center">
            Compare {capitalizeFirstLetter(modelName)} across the{' '}
            {specificationsPrices[0]?.make} range.
          </Text>
        </Box>
        <Box position="relative">
          <Box
            ref={scrollContainerRef}
            className={cn(
              Styles.compareModelsContainer,
              isScrolling && Styles.isScrolling,
              specificationsPrices.length < 4 && Styles.compareModelCenterAlign,
            )}
          >
            {memoizedModelCards}
          </Box>
        </Box>

        <Box className={Styles.modelNavigationBottom}>
          <button
            aria-label="Previous model"
            onClick={scrollLeft}
            className={cn(
              Styles.navButton,
              Styles.prevButton,
              !canScrollLeft && Styles.navButtonDisabled,
            )}
            disabled={!canScrollLeft}
          >
            <ArrowBackwardCircleDark />
          </button>
          <button
            aria-label="Next model"
            onClick={scrollRight}
            className={cn(
              Styles.navButton,
              Styles.nextButton,
              !canScrollRight && Styles.navButtonDisabled,
            )}
            disabled={!canScrollRight}
          >
            <ArrowForwardCircleDark />
          </button>
        </Box>
        {!hideCompareButton && (
          <Box justifyContent="center" mt={8} textAlign="center">
            <LinkCustom
              className="btnOutline"
              href={`/new-cars/${slug}/compare-models?mmcode=${selectedModalMMCode?.slice(0, 2).join(',')}`}
            >
              Compare Models
            </LinkCustom>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CompareModels;
